import React, { useState } from "react"
import ReactLoading from "react-loading"
import { linkifyit } from "linkify-it"
import { RotateCw, ChevronDown, Image } from "styled-icons/feather"

import defaultAvatar from "assets/images/dashboard/avatar-empty-state.png"
import { LinkedInWorldIcon, LinkedInLikeIcon, LinkedInCommentIcon, LinkedInRepostIcon, LinkedInSendIcon } from "./LinkedInIcons"
import PdfCarousel from "./PdfCarousel"
import StaticVisual from "./StaticVisual"
import * as styles from "../styles"

// Mock engagement data
const MOCK_LIKES = 125
const MOCK_REPOSTS = 3
const MOCK_FOLLOWERS = "2,847 followers"

const TEXT_COLLAPSED_LINES = 3

type LinkedInPostPreviewProps = {
  postText: string | null
  postImage?: { url: string; mimeType?: string; html?: string } | null
  userName: string
  userAvatar?: string | null
  // Whether the card is open; gates the heavy visual preview. Defaults to true for non-collapsible usages.
  active?: boolean
  onRegenerate?: () => void
  onChangePicture?: () => void
  isRegenerating?: boolean
  headerAction?: React.ReactNode // rendered at the top right of the card header
}

// fuzzyLink also matches bare domains without a scheme (e.g. "willow.co"), matching how LinkedIn
// itself links them. fuzzyEmail stays off so email addresses keep rendering as plain text.
const linkDetector = linkifyit({ fuzzyLink: true, fuzzyEmail: false })

// LinkedIn replaces URLs with an lnkd.in short link; the preview mimics that display.
const renderTextWithLinks = (text: string, keyPrefix: string): React.ReactNode[] => {
  const matches = linkDetector.match(text)
  if (!matches) return [<span key={keyPrefix}>{text}</span>]

  const nodes: React.ReactNode[] = []
  let cursor = 0
  matches.forEach((match, matchIndex) => {
    if (match.index > cursor) {
      nodes.push(<span key={`${keyPrefix}-text-${matchIndex}`}>{text.slice(cursor, match.index)}</span>)
    }
    // linkify-it prefixes schemeless matches with http://; default to https like the www. branch below.
    const href = match.schema === "" ? `https://${match.raw}` : match.url
    nodes.push(
      <styles.LinkedInLink key={`${keyPrefix}-link-${matchIndex}`} href={href} target="_blank" rel="noopener noreferrer">
        https://lnkd.in/dRdA6zaT
      </styles.LinkedInLink>
    )
    cursor = match.lastIndex
  })
  if (cursor < text.length) {
    nodes.push(<span key={`${keyPrefix}-tail`}>{text.slice(cursor)}</span>)
  }
  return nodes
}

const parseContent = (text: string): React.ReactNode[] => {
  // Match @mentions (including multi-word names like @Koen Verstrepen), #hashtags, and scheme'd
  // URLs. URLs stay in the split so their # and @ characters never collide with the mention and
  // hashtag branches; bare domains are picked up by renderTextWithLinks on the plain segments.
  // Multi-word mentions: @word followed by additional capitalized words
  // (?<!\w) keeps this from matching mid-token, e.g. the "@" in an email address like thomas@hrlegal.be
  const parts = text.split(/(@\[.*?\]|(?<!\w)@\w+(?:\s+[A-Z][a-zA-Z]*)*|#\w+|https?:\/\/[^\s]+|www\.[^\s]+)/g)

  // The explicit type argument keeps TS 4.5 from inferring the element type from the first
  // branch (Element) and rejecting the ReactNode[] segments renderTextWithLinks returns.
  return parts.flatMap<React.ReactNode>((part, index) => {
    if (!part) return []

    if (part.startsWith("@[")) {
      // Multi-word mention: remove @[ and ]
      const name = part.slice(2, -1)
      return [<styles.LinkedInHighlight key={index}>{name}</styles.LinkedInHighlight>]
    } else if (part.startsWith("@")) {
      // Single-word mention
      return [<styles.LinkedInHighlight key={index}>{part.substring(1)}</styles.LinkedInHighlight>]
    } else if (part.startsWith("#")) {
      return [<styles.LinkedInHighlight key={index}>{part}</styles.LinkedInHighlight>]
    } else if (part.startsWith("http://") || part.startsWith("https://") || part.startsWith("www.")) {
      return [
        <styles.LinkedInLink key={index} href={part.startsWith("www.") ? `https://${part}` : part} target="_blank" rel="noopener noreferrer">
          https://lnkd.in/dRdA6zaT
        </styles.LinkedInLink>,
      ]
    }

    return renderTextWithLinks(part, `part-${index}`)
  })
}

const LinkedInPostPreview: React.FC<LinkedInPostPreviewProps> = ({
  postText,
  postImage,
  userName,
  userAvatar,
  active = true,
  onRegenerate,
  onChangePicture,
  isRegenerating = false,
  headerAction,
}) => {
  const normalizedText = (postText || "").trim()

  const [textExpanded, setTextExpanded] = useState(false)
  const collapsedLines: string[] = []
  for (const line of normalizedText.split("\n")) {
    if (line.trim() === "" || collapsedLines.length >= TEXT_COLLAPSED_LINES) break
    collapsedLines.push(line)
  }
  const collapsedText = collapsedLines.join("\n")
  const isLongText = collapsedText.length < normalizedText.length
  const displayText = isLongText && !textExpanded ? collapsedText : normalizedText

  // Missing avatar → default upfront. Broken/expired channel logos (LinkedIn company-logo URLs
  // carry an `e=<expiry>` param and 404 once it passes) can only be caught at load time, so we
  // also swap to the default onError. The flag prevents a loop if the fallback itself errors.
  const avatarSrc = userAvatar || defaultAvatar
  const handleAvatarError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget
    if (img.dataset.fallback) return
    img.dataset.fallback = "true"
    img.src = defaultAvatar
  }

  return (
    <styles.LinkedInPreviewCard data-testid="linkedin-preview">
      {/* Post Header */}
      <styles.LinkedInPreviewHeader data-testid="linkedin-preview-header">
        <styles.LinkedInHeaderContent>
          <styles.LinkedInAvatarWrapper data-testid="linkedin-preview-avatar">
            <styles.LinkedInAvatarImage src={avatarSrc} alt={userName} onError={handleAvatarError} />
          </styles.LinkedInAvatarWrapper>
          <styles.LinkedInHeaderInfo>
            <styles.LinkedInUserName data-testid="linkedin-preview-name">{userName}</styles.LinkedInUserName>
            <styles.LinkedInUserFollowers data-testid="linkedin-preview-followers">{MOCK_FOLLOWERS}</styles.LinkedInUserFollowers>
            <styles.LinkedInPostMeta data-testid="linkedin-preview-timestamp">
              2h • <LinkedInWorldIcon style={{ width: 12, height: 12, verticalAlign: "middle" }} />
            </styles.LinkedInPostMeta>
          </styles.LinkedInHeaderInfo>
        </styles.LinkedInHeaderContent>
        {headerAction}
      </styles.LinkedInPreviewHeader>

      {/* Post Content */}
      <styles.LinkedInPreviewBody>
        {normalizedText && (
          <styles.LinkedInPostContent data-testid="linkedin-preview-text">
            {parseContent(displayText)}
            {isLongText && !textExpanded && (
              <>
                …{" "}
                <styles.LinkedInMoreLink onClick={() => setTextExpanded(true)} data-testid="linkedin-preview-more">
                  more
                </styles.LinkedInMoreLink>
              </>
            )}
            {isLongText && textExpanded && (
              <>
                {" "}
                <styles.LinkedInMoreLink onClick={() => setTextExpanded(false)} data-testid="linkedin-preview-less">
                  less
                </styles.LinkedInMoreLink>
              </>
            )}
          </styles.LinkedInPostContent>
        )}

        {postImage && (
          <styles.LinkedInPostImageWrapper data-testid="linkedin-preview-image">
            {/* Gate the heavy preview on open: collapsed rows stay mounted (grid 0fr collapse), so
                rendering eagerly would fetch every post's PDF/image and spin up an iframe per row. */}
            {active &&
              (postImage.html ? (
                // Edited Create visuals: render the (synchronously-saved) HTML directly so the
                // preview is always current, instead of the async-re-rendered PNG/PDF.
                <StaticVisual html={postImage.html} />
              ) : postImage.mimeType === "application/pdf" ? (
                <PdfCarousel url={postImage.url} />
              ) : (
                <styles.LinkedInPostImage src={postImage.url} alt="Post content" />
              ))}

            {!isRegenerating && (onChangePicture || onRegenerate) && (
              <styles.VisualHoverActions>
                {onChangePicture && (
                  <styles.ChangePictureButton
                    type="button"
                    onClick={onChangePicture}
                    aria-label="Change picture"
                    data-testid="post-change-picture"
                  >
                    <Image size={13} />
                    Change picture
                  </styles.ChangePictureButton>
                )}
                {onRegenerate && (
                  <styles.RegenerateVisualButton
                    type="button"
                    onClick={onRegenerate}
                    aria-label="Regenerate visual"
                    data-testid="post-recreate-visual"
                  >
                    <RotateCw size={13} />
                    Regenerate
                  </styles.RegenerateVisualButton>
                )}
              </styles.VisualHoverActions>
            )}

            {isRegenerating && (
              <styles.RegeneratingOverlay data-testid="post-recreate-visual-loading">
                <styles.RegeneratingBadge>
                  <ReactLoading type="spin" color="#fff" width={20} height={20} />
                  <styles.RegeneratingOverlayLabel>Regenerating…</styles.RegeneratingOverlayLabel>
                </styles.RegeneratingBadge>
              </styles.RegeneratingOverlay>
            )}
          </styles.LinkedInPostImageWrapper>
        )}

        {!normalizedText && !postImage && (
          <styles.LinkedInEmptyContent data-testid="linkedin-preview-empty">
            No content available
          </styles.LinkedInEmptyContent>
        )}

        {/* Action bar — a single line: avatar + caret, actions (with counts), reactions (as in the example) */}
        <styles.LinkedInActionBar data-testid="linkedin-preview-actions">
          <styles.LinkedInActionGroup>
            <styles.LinkedInActionAvatar>
              <styles.LinkedInSmallAvatar>
                <styles.LinkedInAvatarImage src={avatarSrc} alt={userName} onError={handleAvatarError} />
              </styles.LinkedInSmallAvatar>
              <ChevronDown size={12} />
            </styles.LinkedInActionAvatar>
            <styles.LinkedInActionItem data-testid="linkedin-action-like" aria-label="Like">
              <LinkedInLikeIcon style={{ width: 18, height: 18 }} />
              <span data-testid="linkedin-preview-likes">{MOCK_LIKES}</span>
            </styles.LinkedInActionItem>
            <styles.LinkedInActionItem data-testid="linkedin-action-comment" aria-label="Comment">
              <LinkedInCommentIcon style={{ width: 18, height: 18 }} />
            </styles.LinkedInActionItem>
            <styles.LinkedInActionItem data-testid="linkedin-action-repost" aria-label="Repost">
              <LinkedInRepostIcon style={{ width: 18, height: 18 }} />
              <span data-testid="linkedin-preview-reposts">{MOCK_REPOSTS}</span>
            </styles.LinkedInActionItem>
            <styles.LinkedInActionItem data-testid="linkedin-action-send" aria-label="Send">
              <LinkedInSendIcon style={{ width: 18, height: 18 }} />
            </styles.LinkedInActionItem>
          </styles.LinkedInActionGroup>
          <styles.LinkedInLikeIconBadge>
            <LinkedInLikeIcon style={{ width: 10, height: 10, color: "white" }} />
          </styles.LinkedInLikeIconBadge>
        </styles.LinkedInActionBar>
      </styles.LinkedInPreviewBody>
    </styles.LinkedInPreviewCard>
  )
}

export default LinkedInPostPreview
